import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { QRCodeSVG } from "qrcode.react";
import { toast, Toaster } from "sonner";
import { Copy, Share, EnvelopeSimple, ChatCircleText, Link as LinkIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { buildVCard, downloadVCard } from "../lib/vcard";

export default function MyCard() {
  const { user, refreshUser } = useAuth();
  const slug = user?.slug;
  const publicUrl = `${window.location.origin}/c/${slug}`;
  const p = user?.profile || {};
  const name = p.name || user?.name;
  const [refEnabled, setRefEnabled] = useState(!!user?.referral_enabled);
  const [refPrompt, setRefPrompt] = useState(user?.referral_prompt || "Know one person who'd love to connect with me? Drop their info below.");
  const [referrals, setReferrals] = useState([]);
  const [qrMode, setQrMode] = useState("vcard"); // 'vcard' = adds directly to contacts / 'url' = opens landing card

  useEffect(() => { api.get("/referrals").then((r) => setReferrals(r.data)).catch(() => {}); }, []);

  const saveReferralSettings = async () => {
    try { await api.put("/cards/referral-settings", { referral_enabled: refEnabled, referral_prompt: refPrompt }); toast.success("Referral settings saved"); await refreshUser(); }
    catch { toast.error("Save failed"); }
  };

  const copy = async () => { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); };

  const vCard = buildVCard({
    name, email: user?.email, phone: p.phone, title: p.title, company: p.company,
    website: p.website, linkedin: p.linkedin, tagline: p.tagline,
  });

  const qrValue = qrMode === "vcard" ? vCard : publicUrl;

  const downloadVcf = () => downloadVCard(vCard, `${(name || "card").replace(/\s+/g, "_")}.vcf`);

  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent(`Connect with ${name}`)}&body=${encodeURIComponent(`Save my contact info here: ${publicUrl}`)}`);
  const shareSms = () => window.open(`sms:?&body=${encodeURIComponent(`Save my contact: ${publicUrl}`)}`);
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: name, text: "Save my contact info", url: publicUrl }); } catch { /* user canceled share */ } }
    else copy();
  };

  if (!p.name && !user?.name) {
    return <div className="p-10"><p className="text-[#C7DDE5]">Complete your <Link className="underline" to="/profile">profile</Link> first.</p></div>;
  }

  return (
    <div className="p-6 md:p-10" data-testid="mycard-page">
      <Toaster theme="dark" />
      <div className="overline mb-2">// my card</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">Your digital card</h1>
      <p className="text-[#C7DDE5] mb-10 max-w-2xl">Share this URL or QR code. They scan, hit save, and you&apos;re added to their contacts. Edit info in your <Link className="underline" to="/profile">profile</Link>.</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Card preview */}
        <section className="lg:col-span-7 min-w-0">
          <div className="border p-6 md:p-10 grid-bg overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="card-preview">
            <div className="overline mb-6 truncate">// {p.company || "independent"}</div>
            <div
              className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[1.05]"
              style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
              data-testid="card-preview-name"
            >
              {name}
            </div>
            {p.title && <div className="font-mono text-sm mt-4 text-[#C7DDE5] uppercase tracking-wider break-words">{p.title}</div>}
            {p.tagline && <div className="mt-6 text-base md:text-lg max-w-full md:max-w-md break-words">{p.tagline}</div>}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm font-mono">
              {user?.email && <div className="min-w-0"><div className="overline">email</div><div className="mt-1 break-all">{user.email}</div></div>}
              {p.phone && <div className="min-w-0"><div className="overline">phone</div><div className="mt-1 break-all">{p.phone}</div></div>}
              {p.website && <div className="min-w-0"><div className="overline">web</div><div className="mt-1 break-all">{p.website}</div></div>}
              {p.linkedin && <div className="min-w-0"><div className="overline">linkedin</div><div className="mt-1 break-all">{p.linkedin}</div></div>}
            </div>
            <div className="mt-10 pt-6 border-t flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--border)" }}>
              <span className="overline">scan to save</span>
              <span className="font-display font-bold text-sm sm:text-base">MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD</span>
            </div>
          </div>
        </section>

        {/* Share panel */}
        <section className="lg:col-span-5">
          <div className="border p-6 flex flex-col items-center" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between w-full mb-4">
              <div className="overline">// qr code</div>
              <div className="flex items-center gap-0" data-testid="qr-mode-toggle">
                <button onClick={() => setQrMode("vcard")} data-testid="qr-mode-vcard"
                  className="tag cursor-pointer"
                  style={qrMode === "vcard" ? { borderColor: "var(--accent)", color: "var(--accent)" } : {}}>
                  Add to contacts
                </button>
                <button onClick={() => setQrMode("url")} data-testid="qr-mode-url"
                  className="tag cursor-pointer ml-2"
                  style={qrMode === "url" ? { borderColor: "var(--accent)", color: "var(--accent)" } : {}}>
                  Open card page
                </button>
              </div>
            </div>
            <div className="bg-white p-4" data-testid="qr-code">
              <QRCodeSVG value={qrValue} size={200} bgColor="#FFFFFF" fgColor="#0A1418" level="M" />
            </div>
            <p className="text-xs mt-3 text-center" style={{ color: "var(--text-2)" }}>
              {qrMode === "vcard"
                ? "Camera scan → phone prompts to save your contact directly (name, email, phone auto-fill)."
                : "Camera scan → opens your full card page with bio + referral form."}
            </p>
            <div className="mt-6 w-full">
              <div className="overline mb-2">// public url</div>
              <div className="flex gap-2">
                <input readOnly value={publicUrl} className="input-base flex-1" style={{ border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="public-url" />
                <button onClick={copy} className="btn-secondary flex items-center gap-2" data-testid="copy-url-btn"><Copy weight="bold" /></button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-0 mt-0 border-l" style={{ borderColor: "var(--border)" }}>
            <button onClick={shareEmail} data-testid="share-email-btn" className="btn-secondary flex items-center justify-center gap-2 border-l-0 border-t-0"><EnvelopeSimple weight="bold" /> Email</button>
            <button onClick={shareSms} data-testid="share-sms-btn" className="btn-secondary flex items-center justify-center gap-2 border-t-0"><ChatCircleText weight="bold" /> SMS</button>
            <button onClick={nativeShare} data-testid="share-native-btn" className="btn-secondary flex items-center justify-center gap-2 border-l-0"><Share weight="bold" /> Share</button>
            <button onClick={downloadVcf} data-testid="download-vcf-btn" className="btn-secondary flex items-center justify-center gap-2"><LinkIcon weight="bold" /> .vcf</button>
          </div>

          <div className="border mt-8 p-6" style={{ borderColor: "var(--border)" }} data-testid="referral-settings">
            <div className="flex items-center justify-between mb-3">
              <div className="overline" style={{ color: "var(--accent)" }}>// referral mode</div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={refEnabled} onChange={(e) => setRefEnabled(e.target.checked)} data-testid="referral-toggle" className="w-4 h-4" />
                <span className="text-sm font-mono">{refEnabled ? "ON" : "OFF"}</span>
              </label>
            </div>
            <p className="text-sm text-[#C7DDE5] mb-3">When ON, anyone who saves your card is also prompted to refer one person who would benefit from knowing you.</p>
            {refEnabled && (
              <>
                <label className="label-base">Prompt shown to visitors</label>
                <textarea rows={2} value={refPrompt} onChange={(e) => setRefPrompt(e.target.value)} className="input-base mb-3" style={{ border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="referral-prompt" />
              </>
            )}
            <button onClick={saveReferralSettings} data-testid="save-referral-btn" className="btn-primary text-sm w-full">Save settings</button>
            {referrals.length > 0 && (
              <div className="mt-4">
                <div className="overline mb-2">// referrals received · {referrals.length}</div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {referrals.slice(0, 6).map((r) => (
                    <div key={r.id} className="text-sm border p-2" style={{ borderColor: "var(--border)" }}>
                      <div className="font-semibold">{r.referral_name}</div>
                      <div className="overline">from {r.from_name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

